import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IOrderUser } from 'src/app/features/orders/models/order.model';
import { IQuotationFilter, IQuotationLocationFilter, IQuotationProductFilter } from '../../models/quotations.models';
import { parseQuotationFilterRequest, refillQuotationFilter } from 'src/app/shared/utils/helpers';
import { FetchAllCatalogs } from 'src/app/features/catalogs/store/catalogs.actions';
import { ICatalog } from 'src/app/features/catalogs/models/catalogs.models';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { FormControl } from '@angular/forms';
import { OrdersService } from 'src/app/features/orders/services/orders.services';
import { selectHasOwnDataScope } from 'src/app/core/store/permissions/permissions.selectors';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { ClearProducts, FetchProductsBySearch } from 'src/app/features/produits/store/produits.actions';
import { FetchTeamOptions } from 'src/app/features/settings/store/settings.actions';
import { ITeamOption } from 'src/app/features/settings/models/settings.models';

@Component({
  selector: 'app-filter-quotations',
  templateUrl: './filter-quotations.component.html',
  styleUrls: ['./filter-quotations.component.scss'],
})
export class FilterQuotationsComponent implements OnInit, OnDestroy {
  currentFilter: Observable<IQuotationFilter> = this.store.select(
    (state) => state.quotations.filterState.advanced
  );

  allCatalogs$: Observable<ICatalog[]> = this.store.select((state) => state.catalogs.allCatalogs);
  filteredCatalogs$!: Observable<ICatalog[]>;
  catalogSearchCtrl = new FormControl('');

  // `teamOptions`, not `allTeams`: the latter is fed by `GET /v1/teams`, which is 403
  // without `access:settings_team`, and this panel is used by ordinary order users.
  teamOptions$: Observable<ITeamOption[]> = this.store.select(
    (state) => state.settings?.teamOptions ?? []
  );
  filteredTeams$!: Observable<ITeamOption[]>;
  teamSearchCtrl = new FormControl('');

  filter: IQuotationFilter = refillQuotationFilter();
  isOwnershipScopeForced$: Observable<boolean> = this.store.select(selectHasOwnDataScope('order'));
  isOwnershipScopeForced = false;

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  // "Contains product(s)" autocomplete — searches by SKU or name, filters by the
  // picked product. Reads the shared product search slice.
  productOptions$: Observable<IProduct[]> = this.store.select(
    (state: any) => state.products.productsBySearch
  );

  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;

  productNameSearchInput = new FormControl<string>('', { nonNullable: true });
  productNameSearchLoading = false;
  private productOptionJustSelected = false;

  ownerSearchInput = new FormControl<string>('', { nonNullable: true });
  creatorSearchInput = new FormControl<string>('', { nonNullable: true });
  ownerOptions: IOrderUser[] = [];
  creatorOptions: IOrderUser[] = [];
  ownerSearchLoading = false;
  creatorSearchLoading = false;

  private subs: Subscription[] = [];

  sourceOptions = [
    { value: 'email', label: 'quotations_and_orders.email' },
    { value: 'BO portal', label: 'quotations_and_orders.backoffice' },
    { value: 'B2B portal', label: 'quotations_and_orders.b2b_portal' },
    { value: 'Agent portal', label: 'quotations_and_orders.agent_portal' },
  ];

  dialogOptions: SharedDialogOptions = {
    titleIcon: 'filter_icon',
    cancelText: 'CLOSE',
    cancelButtonStyle: 'secondary-btn',
    middleText: 'RESET FILTERS',
    middleIcon: 'rollback',
    middleButtonStyle: 'secondary-btn',
    confirmText: 'APPLY',
    confirmButtonStyle: 'primary',
    size: 'large',
  };

  constructor(
    public dialogRef: MatDialogRef<FilterQuotationsComponent>,
    private store: Store<AppState>,
    private ordersService: OrdersService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.isOwnershipScopeForced$.subscribe((forced) => {
        this.isOwnershipScopeForced = forced;
      })
    );

    this.subs.push(
      this.currentFilter.subscribe((f: IQuotationFilter) => {
        this.filter = refillQuotationFilter(f);
        this.ensureOwnershipFilter();
        const own = this.filter.ownershipFilter;
        this.ownerSearchInput.setValue(own?.owner?.name ?? '', { emitEvent: false });
        this.creatorSearchInput.setValue(own?.creator?.name ?? '', { emitEvent: false });
        // The box is a SEARCH box, not the display of the selection: the chips render
        // `filter.ownershipFilter.teams` directly, which is what answers the FILT-03
        // symptom (a box reading empty while the filter is still applied) now that a
        // selection can hold more than one name.
        this.teamSearchCtrl.setValue('', { emitEvent: false });
      })
    );
    this.store.dispatch(FetchAllCatalogs());
    this.store.dispatch(FetchTeamOptions());

    this.filteredCatalogs$ = combineLatest([
      this.catalogSearchCtrl.valueChanges.pipe(startWith('')),
      this.allCatalogs$,
    ]).pipe(
      map(([search, catalogs]) => {
        const term = (search || '').toLowerCase();
        if (!term) return catalogs;
        return catalogs.filter((c) => c.name.toLowerCase().includes(term));
      })
    );

    // Same shape as the catalogs above: teams are few and loaded wholesale, so the list is
    // narrowed client-side. There is no team search endpoint and one is not warranted.
    this.filteredTeams$ = combineLatest([
      this.teamSearchCtrl.valueChanges.pipe(startWith('')),
      this.teamOptions$,
    ]).pipe(
      map(([search, teams]) => {
        // Material writes the RAW OPTION OBJECT to this control before (optionSelected)
        // fires: `_setValueAndClose` calls `_onChange(source.value)` and only then
        // `_emitSelectEvent`, and there is no `displayWith` to stringify it. Without this
        // guard the object reaches `.toLowerCase()`, the map THROWS, and the error kills
        // this observable for good -- the `| async` in the template never emits again, so
        // the dropdown silently stops updating for the rest of the dialog session.
        // `ownerSearchInput` and `creatorSearchInput` below both guard the same way.
        if (typeof search !== 'string') return teams;
        const term = search.toLowerCase();
        if (!term) return teams;
        return teams.filter((t) => t.name.toLowerCase().includes(term));
      })
    );

    // There is deliberately NO subscription clearing the selection when the text stops
    // naming it. That guard existed only while the box doubled as the display of a single
    // pick; with chips it would delete a valid selection the moment the user types a
    // second search term. The chips and their `×` are the only way to change a selection.

    this.subs.push(
      this.ownerSearchInput.valueChanges
        .pipe(
          startWith(this.ownerSearchInput.value),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((text) => {
            if (typeof text !== 'string') return of(this.ownerOptions);
            const v = text.trim();
            const sel = this.filter.ownershipFilter?.owner;
            if (sel && v !== sel.name) {
              this.filter.ownershipFilter!.owner = undefined;
            }
            this.ownerSearchLoading = true;
            return this.ordersService.searchBackofficeUsers(v, 50).pipe(
              finalize(() => {
                this.ownerSearchLoading = false;
              })
            );
          })
        )
        .subscribe((users) => {
          this.ownerOptions = users;
        })
    );

    this.subs.push(
      this.creatorSearchInput.valueChanges
        .pipe(
          startWith(this.creatorSearchInput.value),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((text) => {
            if (typeof text !== 'string') return of(this.creatorOptions);
            const v = text.trim();
            const sel = this.filter.ownershipFilter?.creator;
            if (sel && v !== sel.name) {
              this.filter.ownershipFilter!.creator = undefined;
            }
            this.creatorSearchLoading = true;
            return this.ordersService.searchBackofficeUsers(v, 50).pipe(
              finalize(() => {
                this.creatorSearchLoading = false;
              })
            );
          })
        )
        .subscribe((users) => {
          this.creatorOptions = users;
        })
    );

    this.subs.push(
      this.productNameSearchInput.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((text) => {
          this.productNameSearchLoading = this.searchProducts(text);
        })
    );

    this.subs.push(
      this.productOptions$.subscribe(() => {
        this.productNameSearchLoading = false;
      })
    );
  }

  /**
   * Dispatch a product search for the given keyword and return whether a
   * request was issued (i.e. the matching field should show a loading hint).
   */
  private searchProducts(text: string | null): boolean {
    const keyword = (text || '').trim();
    if (!keyword) {
      this.store.dispatch(ClearProducts());
      return false;
    }
    this.store.dispatch(
      FetchProductsBySearch({
        keyword,
        filter: { filters: [{ key: 'status', operator: 'equals', value: 'Published' }] },
        includeDeals: false,
        page: { number: 1, size: 10 },
        append: false,
        skipGlobalLoader: true,
      })
    );
    return true;
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.store.dispatch(ClearProducts());
  }

  private ensureOwnershipFilter(): void {
    if (!this.filter.ownershipFilter) {
      this.filter.ownershipFilter = {};
    }
  }

  onOwnerSelected(event: MatAutocompleteSelectedEvent): void {
    const u = event.option.value as IOrderUser;
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.owner = u;
    this.ownerSearchInput.setValue(u.name, { emitEvent: false });
    this.ownerOptions = [];
  }

  onCreatorSelected(event: MatAutocompleteSelectedEvent): void {
    const u = event.option.value as IOrderUser;
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.creator = u;
    this.creatorSearchInput.setValue(u.name, { emitEvent: false });
    this.creatorOptions = [];
  }

  clearOwnerFilter(): void {
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.owner = undefined;
    this.ownerSearchInput.setValue('', { emitEvent: true });
  }

  clearCreatorFilter(): void {
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.creator = undefined;
    this.creatorSearchInput.setValue('', { emitEvent: true });
  }

  // Chip stores both: `name` is shown to the user, `sku` is sent to the backend.
  // Dedup by sku (the filter value).
  private pushProduct(product: IQuotationProductFilter): void {
    const sku = (product.sku || '').trim();
    const name = (product.name || '').trim();
    if (!sku && !name) return;
    if (!this.filter.quotationInfoFilter.products) {
      this.filter.quotationInfoFilter.products = [];
    }
    if (!this.filter.quotationInfoFilter.products.some((p) => p.sku === sku)) {
      this.filter.quotationInfoFilter.products = [
        ...this.filter.quotationInfoFilter.products,
        { sku, name: name || sku },
      ];
    }
  }

  // Free-typed value (Enter / comma) — treated as a SKU; the chip shows it as-is.
  addProduct(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) this.pushProduct({ sku: value, name: value });
    event.chipInput?.clear();
    this.clearProductInput();
    this.store.dispatch(ClearProducts());
  }

  // Product chosen from the autocomplete suggestions: keep its name + sku.
  onProductOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.productOptionJustSelected = true;
    const product = event.option.value as IProduct;
    this.pushProduct({ sku: product?.sku, name: product?.name });
    this.clearProductInput();
    this.store.dispatch(ClearProducts());
  }

  // Confirm the typed value on blur, unless the blur was caused by picking a
  // suggestion. The blur fires before the option's click, so defer one tick and
  // skip when an option was just selected (its value is already chipped).
  onProductBlur(): void {
    setTimeout(() => {
      if (this.productOptionJustSelected) {
        this.productOptionJustSelected = false;
        return;
      }
      const value = this.nameInput?.nativeElement.value;
      if (value && value.trim()) {
        this.pushProduct({ sku: value.trim(), name: value.trim() });
        this.clearProductInput();
        this.store.dispatch(ClearProducts());
      }
    }, 200);
  }

  private clearProductInput(): void {
    if (this.nameInput) {
      this.nameInput.nativeElement.value = '';
    }
    this.productNameSearchInput.setValue('', { emitEvent: false });
  }

  removeProduct(product: IQuotationProductFilter): void {
    this.filter.quotationInfoFilter.products = (
      this.filter.quotationInfoFilter.products || []
    ).filter((p) => p.sku !== product.sku);
  }

  /**
   * Removes one chip from the location scope injected by "View all orders" on a
   * company page. Read-only by design: the scope is injected, never composed
   * here, so there is no matching "add".
   */
  removeLocation(location: IQuotationLocationFilter): void {
    this.filter.organizeFilter.locations = (this.filter.organizeFilter.locations || []).filter(
      (l) => l.id !== location.id
    );
  }

  getFiltersCount(): number {
    return parseQuotationFilterRequest(this.filter)?.filters?.length || 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  resetFilters(): void {
    this.filter = refillQuotationFilter();
    // Explicit, though `refillQuotationFilter()` already returns an empty ownership branch:
    // a cleared panel that silently keeps narrowing to a team is the failure worth pinning.
    // `delete`, not `= undefined`: the latter leaves an own `teams` key on the object, which
    // is a different shape from a fresh refill and would be sent as an explicit null.
    if (this.filter.ownershipFilter) delete this.filter.ownershipFilter.teams;
    this.catalogSearchCtrl.setValue('');
    this.teamSearchCtrl.setValue('');
    this.ownerSearchInput.setValue('', { emitEvent: false });
    this.creatorSearchInput.setValue('', { emitEvent: false });
    this.productNameSearchInput.setValue('', { emitEvent: false });
    this.store.dispatch(ClearProducts());
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }

  selectCatalog(catalog: ICatalog): void {
    this.filter.organizeFilter.catalogs = [catalog];
    this.catalogSearchCtrl.setValue(catalog.name);
  }

  clearCatalog(): void {
    this.filter.organizeFilter.catalogs = [];
    this.catalogSearchCtrl.setValue('');
  }

  displayCatalog(catalog: ICatalog): string {
    return catalog?.name || '';
  }

  // Stored as `{ id, name }`, not a bare id: `name` is what each chip shows after a URL
  // round-trip, and an object survives `removeEmptyProperties`/`deepConvertBooleans` intact.
  //
  // ADDITIVE. Picking a second team must keep the first -- assigning here is the
  // single-select bug WB-7898 fixes. Material also re-fires (optionSelected) for a click
  // on an already-chosen option, so the id check is what stops a duplicate chip.
  onTeamSelected(event: MatAutocompleteSelectedEvent): void {
    const team = event.option.value as ITeamOption;
    this.ensureOwnershipFilter();
    const own = this.filter.ownershipFilter!;
    const selected = own.teams ?? [];
    if (!selected.some((t) => t.id === team.id)) {
      // A new array rather than a push: the panel's filter object goes through a JSON
      // round-trip and change detection, and in-place mutation hides the update.
      own.teams = [...selected, { id: team.id, name: team.name }];
    }
    // The box is a search box and the chips are the selection, so it clears rather than
    // echoing the pick -- echoing would narrow the option list to the team just chosen.
    //
    // The clear EMITS on purpose. Suppressing it leaves `filteredTeams$` narrowed by the
    // term used to find the team just picked, so the next dropdown opens filtered by a
    // box that visually reads empty and can hide the second team the user came to pick.
    // Emitting is safe only because the old valueChanges subscription that cleared the
    // selection on text edits is gone -- see the note where it used to live.
    this.teamSearchCtrl.setValue('');
  }

  removeTeam(team: ITeamOption): void {
    this.ensureOwnershipFilter();
    const own = this.filter.ownershipFilter!;
    const remaining = (own.teams ?? []).filter((t) => t.id !== team.id);
    if (remaining.length) {
      own.teams = remaining;
      return;
    }
    // delete, not `= undefined`: an own property holding undefined survives as a present
    // key through the filter's JSON round-trip, so a cleared selection can reappear. An
    // empty array is worse still -- `refillQuotationFilter`'s JSON round-trip KEEPS it,
    // which breaks the `resets every criterion` toEqual against a fresh refill.
    delete own.teams;
  }
}
